import Phaser from 'phaser';
import {
  CREATOR_ITEMS,
  EMPTY_SELECTION,
  MAX_PIECES,
  MAX_PLOTS,
  SCALE_MAX,
  SCALE_MIN,
  TEXT_DEFAULT,
  TEXT_KEY,
  TEXT_MAX,
  cleanText,
  copySelection,
  findBackground,
  findTrack,
  flipPieces,
  layerFor,
  onMapPlots,
  pasteClip,
  projectToLevel,
  rectFrom,
  stepLayer,
  selectInRect,
  selectionSize,
  snapGrid,
  snapPoint,
  projectPictureKey,
  type CreatorClip,
  type CreatorPiece,
  type CreatorProject,
  type CreatorSelection,
  type ItemTab,
} from '../../config/creator';
import type { Vec2 } from '../../core/vec';
import { setCreatorLevel } from '../../config/testArena';
import { creatorStoreNow } from '../creator/storeAccess';
import { ScrollList } from '../../ui/scrollList';
import { drawTrailStyle } from '../trackDraw';
import {
  PIECE_DEPTH,
  makePieceImage,
  placePiece,
  sizePiece,
  textureColorGroups,
  type PieceObject,
} from '../creator/pieces';
import { canvasTextInput } from '../creator/textInput';
import { PALETTE } from '../creator/recolor';
import {
  CREATOR_ORANGE,
  CREATOR_PANEL,
  CREATOR_TEAL,
  creatorButton,
  creatorFlash,
} from '../creator/creatorUi';

const SIDE_W = 300;
const VIEW_W = 1280 - SIDE_W;
const ZOOMS = [0.5, 0.625, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
/** The editor world: the map plus a full map's margin on every side. */
const WORLD = { x: -1280, y: -720, w: 3840, h: 2160 };
const UI_DEPTH = 100;
const TILE = 124;

export const NO_WAVES = 'Make a wave first — press WAVES';
export const NO_PLOTS = 'Place a plot first — it is under SPECIAL';

/** Where each selected thing started when a group move began. */
interface MoveStart {
  at: Vec2;
  pieces: Map<string, Vec2>;
  plots: Map<number, Vec2>;
}

let pieceSeq = 0;
/** A fresh piece id (a token the server accepts). */
function newPieceId(): string {
  pieceSeq = (pieceSeq + 1) % 1e6;
  return `u-${Date.now().toString(36)}${pieceSeq.toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
}

/**
 * The Creation Editor (spec 2026-11-01). The map fills the view right of the
 * side menu; the side menu's DECOR / TOP / SPECIAL lists drag pieces onto it.
 * SELECT toggles select mode: tap a piece (or drag a green box over several)
 * to select, drag to move; ▲ ▼ FLIP DELETE along the top, and for a single
 * piece 🎨, SCALE, ROTATE (and EDIT for a textbox). Ctrl/⌘+C and +V copy and
 * paste at the mouse. Outside select mode, dragging the map pans. Two cameras: the main one shows the map
 * (zoomable, scrollable), a fixed UI camera draws the menus over it — every
 * object is assigned to exactly one of them as it is created.
 */
export class CreatorEditorScene extends Phaser.Scene {
  project: CreatorProject | null = null;
  selection: CreatorSelection = EMPTY_SELECTION;
  selectMode = false;
  /** What Ctrl+C copied (kept across scenes, like a real clipboard). */
  static clip: CreatorClip | null = null;
  private projectId = '';
  private uiCam!: Phaser.Cameras.Scene2D.Camera;
  private buildingWorld = false;
  private zoomIndex = 2;
  private tab: ItemTab = 'decor';
  private list?: ScrollList;
  private listObjs: Phaser.GameObjects.GameObject[] = [];
  private pieceImages = new Map<string, PieceObject>();
  private plotImages: Phaser.GameObjects.Image[] = [];
  private outline?: Phaser.GameObjects.Graphics;
  private worldBase: Phaser.GameObjects.GameObject[] = [];
  private selectTools: Phaser.GameObjects.GameObject[] = [];
  private bar: Phaser.GameObjects.GameObject[] = [];
  private barTool: 'scale' | 'rotate' | null = null;
  private panel: Phaser.GameObjects.GameObject[] = [];
  private selectBtn?: { bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text };
  private pan: { x: number; y: number; sx: number; sy: number } | null = null;
  private moving: MoveStart | null = null;
  private marquee: { from: Vec2; box: Phaser.GameObjects.Graphics } | null = null;
  private ghost?: Phaser.GameObjects.Image;
  /** Edits since the last save (read by tests; SAVE/TEST/WAVES all save). */
  dirty = false;
  private busy = false;

  constructor() {
    super({ key: 'CreatorEditorScene' });
  }

  init(data: { id?: string }): void {
    this.projectId = data?.id ?? this.projectId;
  }

  create(): void {
    this.project = null;
    this.selection = EMPTY_SELECTION;
    this.selectMode = false;
    this.marquee = null;
    this.pieceImages = new Map();
    this.plotImages = [];
    this.worldBase = [];
    this.selectTools = [];
    this.bar = [];
    this.barTool = null;
    this.panel = [];
    this.listObjs = [];
    this.pan = null;
    this.moving = null;
    this.dirty = false;
    this.busy = false;
    this.zoomIndex = 2;

    const main = this.cameras.main;
    main.setViewport(SIDE_W, 0, VIEW_W, 720);
    main.setBounds(WORLD.x, WORLD.y, WORLD.w, WORLD.h);
    main.setBackgroundColor('#0b0a12');
    this.uiCam = this.cameras.add(0, 0, 1280, 720, false, 'creator-ui');
    // Every object belongs to exactly one camera, decided as it is created.
    const onAdded = (obj: Phaser.GameObjects.GameObject) => {
      if (this.buildingWorld) this.uiCam.ignore(obj);
      else main.ignore(obj);
    };
    this.events.on(Phaser.Scenes.Events.ADDED_TO_SCENE, onAdded);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.ADDED_TO_SCENE, onAdded);
      this.list?.destroy();
      this.list = undefined;
    });

    this.buildUi();
    this.installInput();
    void this.loadProject();
  }

  // ── Loading and saving ─────────────────────────────────────────────────────

  private async loadProject(): Promise<void> {
    let all: CreatorProject[];
    try {
      all = await (await creatorStoreNow()).list();
    } catch (e) {
      if (!this.sys.isActive()) return;
      creatorFlash(this, e instanceof Error ? e.message : 'Try again', 360, '#ffcf6b');
      this.time.delayedCall(1600, () => this.scene.start('CreatorMenuScene'));
      return;
    }
    if (!this.sys.isActive()) return;
    const p = all.find((x) => x.id === this.projectId);
    if (!p) {
      this.scene.start('CreatorMenuScene');
      return;
    }
    this.project = p;
    this.drawWorld();
    this.applyZoom(true);
  }

  private async save(): Promise<boolean> {
    if (!this.project) return false;
    try {
      this.project = await (await creatorStoreNow()).save(this.project);
      this.dirty = false;
      return true;
    } catch (e) {
      if (this.sys.isActive())
        creatorFlash(this, e instanceof Error ? e.message : 'Could not save');
      return false;
    }
  }

  private async saveAnd(go: () => void): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const ok = await this.save();
    this.busy = false;
    if (ok && this.sys.isActive()) go();
  }

  // ── The world (map) ────────────────────────────────────────────────────────

  private world<T>(make: () => T): T {
    this.buildingWorld = true;
    try {
      return make();
    } finally {
      this.buildingWorld = false;
    }
  }

  private drawWorld(): void {
    const p = this.project!;
    for (const o of this.worldBase) o.destroy();
    this.worldBase = [];
    const base = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      this.worldBase.push(o);
      return o;
    };
    this.world(() => {
      // The editor margin, dimmed, so the map's edge is obvious.
      base(this.add.rectangle(640, 360, WORLD.w, WORLD.h, 0x1a1d24).setDepth(-2));
      const bg = findBackground(p.background);
      if (bg.key && this.textures.exists(bg.key)) {
        base(this.add.image(640, 360, bg.key).setDisplaySize(1280, 720).setDepth(0));
      } else {
        base(this.add.rectangle(640, 360, 1280, 720, bg.color ?? 0xffffff).setDepth(0));
      }
      const pic = projectPictureKey(p);
      if (pic && this.textures.exists(pic)) {
        base(this.add.image(640, 360, pic).setDisplaySize(1280, 720).setDepth(0.5));
      }
      const track = findTrack(p.track);
      const g = base(this.add.graphics().setDepth(1));
      drawTrailStyle(g, track.path, track.trackStyle);
      for (const lead of track.entrances ?? []) drawTrailStyle(g, lead, track.trackStyle);
      const mouths = track.entrances?.length ? track.entrances.map((l) => l[0]) : [track.path[0]];
      for (const m of mouths) base(this.add.circle(m.x, m.y, 14, 0x3fd16a).setDepth(1.5));
      const end = track.path[track.path.length - 1];
      base(this.add.circle(end.x, end.y, 14, 0xe04848).setDepth(1.5));
      base(this.add.rectangle(640, 360, 1280, 720).setStrokeStyle(4, 0xffffff, 0.8).setDepth(3));
      this.outline = base(this.add.graphics().setDepth(6));
    });
    this.redrawPieces();
    this.redrawPlots();
  }

  private redrawPieces(): void {
    for (const img of this.pieceImages.values()) img.destroy();
    this.pieceImages.clear();
    this.project!.pieces.forEach((pc, i) => this.addPieceImage(pc, i));
    this.drawOutline();
  }

  private addPieceImage(pc: CreatorPiece, order: number): void {
    const img = this.world(() => makePieceImage(this, pc));
    // A glow's shader would draw into the UI camera too; the world owns it.
    img.setDepth(PIECE_DEPTH[pc.layer] + order * 1e-5).setName(`creator-piece-${pc.id}`);
    this.pieceImages.set(pc.id, img);
  }

  private redrawPlots(): void {
    for (const img of this.plotImages) img.destroy();
    this.plotImages = this.project!.plots.map((pt, i) =>
      this.world(() =>
        this.add.image(pt.x, pt.y, 'plot').setDepth(2).setAlpha(0.9).setName(`creator-plot-${i}`),
      ),
    );
    this.drawOutline();
  }

  // ── The UI ─────────────────────────────────────────────────────────────────

  // The list's icons are masked, not removed: one scrolled out of view still
  // takes pointer events, so the buttons under the list sit above its depth.
  private buildUi(): void {
    this.add
      .rectangle(SIDE_W / 2, 360, SIDE_W, 720, CREATOR_PANEL, 1)
      .setDepth(UI_DEPTH - 1)
      .setInteractive();
    this.add.rectangle(SIDE_W, 360, 3, 720, CREATOR_TEAL).setDepth(UI_DEPTH);
    const tabs: [ItemTab, string][] = [
      ['decor', 'DECOR'],
      ['top', 'TOP'],
      ['special', 'SPECIAL'],
    ];
    tabs.forEach(([tab, label], i) => {
      const b = creatorButton(
        this,
        52 + i * 98,
        36,
        label,
        {
          name: `creator-tab-${tab}`,
          width: 92,
          height: 44,
          fontSize: 16,
          depth: UI_DEPTH,
          fill: 0x262234,
        },
        () => {
          this.tab = tab;
          this.buildList();
        },
      );
      b.bg.setData('tab', tab);
    });
    this.buildList();

    creatorButton(
      this,
      75,
      676,
      'TEST',
      { name: 'creator-test', width: 130, height: 54, depth: UI_DEPTH + 5, fill: 0x3fa34d },
      () => void this.test(),
    );
    this.selectBtn = creatorButton(
      this,
      225,
      676,
      'SELECT',
      { name: 'creator-select', width: 130, height: 54, depth: UI_DEPTH + 5, fill: 0x262234 },
      () => this.setSelectMode(!this.selectMode),
    );

    creatorButton(
      this,
      1000,
      40,
      'WAVES',
      { name: 'creator-waves', width: 120, height: 50, depth: UI_DEPTH },
      () => void this.saveAnd(() => this.scene.start('CreatorWavesScene', { id: this.projectId })),
    );
    creatorButton(
      this,
      1130,
      40,
      'SAVE',
      { name: 'creator-save', width: 120, height: 50, depth: UI_DEPTH, fill: CREATOR_ORANGE },
      () => void this.saveAnd(() => this.scene.start('CreatorMenuScene')),
    );
    creatorButton(
      this,
      1235,
      40,
      '⚙',
      {
        name: 'creator-gear',
        width: 56,
        height: 50,
        fontSize: 28,
        depth: UI_DEPTH,
        fill: 0x5c6168,
      },
      () =>
        void this.saveAnd(() => this.scene.start('CreatorSettingsScene', { id: this.projectId })),
    );
    creatorButton(
      this,
      1235,
      610,
      '+',
      {
        name: 'creator-zoom-in',
        width: 56,
        height: 50,
        fontSize: 30,
        depth: UI_DEPTH,
        fill: 0x262234,
      },
      () => this.zoomBy(1),
    );
    creatorButton(
      this,
      1235,
      668,
      '−',
      {
        name: 'creator-zoom-out',
        width: 56,
        height: 50,
        fontSize: 30,
        depth: UI_DEPTH,
        fill: 0x262234,
      },
      () => this.zoomBy(-1),
    );
  }

  private buildList(): void {
    this.list?.destroy();
    for (const o of this.listObjs) o.destroy();
    this.listObjs = [];
    this.children.list
      .filter((o) => (o as Phaser.GameObjects.Rectangle).getData?.('tab') !== undefined)
      .forEach((o) => {
        const r = o as Phaser.GameObjects.Rectangle;
        r.setFillStyle(r.getData('tab') === this.tab ? CREATOR_TEAL : 0x262234);
      });
    const list = new ScrollList(this, {
      x: 14,
      y: 70,
      width: 254,
      height: 560,
      barX: 284,
      depth: UI_DEPTH,
      contentDrag: false,
    });
    this.list = list;
    const items = CREATOR_ITEMS.filter((i) => i.tab === this.tab);
    items.forEach((it, i) => {
      const x = (i % 2) * (TILE + 6) + TILE / 2;
      const y = Math.floor(i / 2) * (TILE + 6) + TILE / 2;
      const cell = this.add.rectangle(x, y, TILE, TILE, 0x262234).setStrokeStyle(1, 0x3a3f47);
      const key = this.textures.exists(it.key) ? it.key : '__MISSING';
      const icon = this.add.image(x, y - 10, key);
      icon.setScale(78 / Math.max(icon.width, icon.height, 1));
      icon
        .setName(`creator-item-${it.key}`)
        .setInteractive({ useHandCursor: true, draggable: true });
      icon.setData('item', it.key);
      const label = this.add
        .text(x, y + TILE / 2 - 14, it.label, {
          fontSize: '13px',
          color: '#e8f4f1',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      list.content.add([cell, icon, label]);
    });
    list.setContentHeight(Math.ceil(items.length / 2) * (TILE + 6));
  }

  // ── Input: pan, drag from the list, select and move ───────────────────────

  private installInput(): void {
    this.input.on('dragstart', (ptr: Phaser.Input.Pointer, obj: Phaser.GameObjects.Image) => {
      const key = obj.getData('item') as string | undefined;
      if (!key || !this.project || !this.list?.inView(ptr)) return;
      this.ghost = this.add
        .image(ptr.x, ptr.y, obj.texture.key)
        .setScale(obj.scale * 1.1)
        .setAlpha(0.85)
        .setDepth(UI_DEPTH + 50);
      this.ghost.setData('item', key);
    });
    this.input.on('drag', (ptr: Phaser.Input.Pointer) => {
      this.ghost?.setPosition(ptr.x, ptr.y);
    });
    this.input.on('dragend', (ptr: Phaser.Input.Pointer) => {
      const g = this.ghost;
      this.ghost = undefined;
      if (!g) return;
      const key = g.getData('item') as string;
      g.destroy();
      if (ptr.x <= SIDE_W) return; // dropped back on the menu: cancelled
      const w = this.cameras.main.getWorldPoint(ptr.x, ptr.y);
      this.place(key, Math.round(w.x), Math.round(w.y));
    });

    this.input.on(
      'pointerdown',
      (ptr: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]) => {
        if (!this.project || ptr.x <= SIDE_W || this.panel.length > 0) return;
        // A press on a UI button over the map belongs to the button.
        if (over.some((o) => (o.cameraFilter & this.cameras.main.id) !== 0)) return;
        const w = this.cameras.main.getWorldPoint(ptr.x, ptr.y);
        if (this.selectMode) {
          const hit = this.hitTest(w.x, w.y);
          if (!hit) {
            // Empty map: draw a selection box.
            this.selection = EMPTY_SELECTION;
            this.refreshSelectTools();
            this.marquee?.box.destroy();
            const box = this.world(() => this.add.graphics().setDepth(7));
            box.setName('creator-marquee');
            this.marquee = { from: { x: w.x, y: w.y }, box };
            return;
          }
          // A press inside the current group moves the group; else just this.
          if (!this.isSelected(hit)) {
            this.selection = hit;
            this.refreshSelectTools();
          }
          this.moving = this.moveStart(w);
          return;
        }
        this.pan = {
          x: ptr.x,
          y: ptr.y,
          sx: this.cameras.main.scrollX,
          sy: this.cameras.main.scrollY,
        };
      },
    );
    this.input.on('pointermove', (ptr: Phaser.Input.Pointer) => {
      if (!ptr.isDown) return;
      const cam = this.cameras.main;
      if (this.pan) {
        cam.setScroll(
          this.pan.sx - (ptr.x - this.pan.x) / cam.zoom,
          this.pan.sy - (ptr.y - this.pan.y) / cam.zoom,
        );
        return;
      }
      const w = cam.getWorldPoint(ptr.x, ptr.y);
      if (this.marquee) {
        const r = rectFrom(this.marquee.from, w);
        const g = this.marquee.box;
        g.clear();
        g.lineStyle(3 / cam.zoom, 0x3fd16a, 1);
        g.strokeRect(r.x, r.y, r.w, r.h);
        return;
      }
      if (this.moving) this.moveSelectionTo(w);
    });
    // Let go outside the canvas: the same as letting go on it.
    this.input.on('pointerupoutside', (ptr: Phaser.Input.Pointer) =>
      this.input.emit('pointerup', ptr, []),
    );
    this.input.on('pointerup', (ptr: Phaser.Input.Pointer) => {
      this.pan = null;
      this.moving = null;
      const m = this.marquee;
      if (m && this.project) {
        this.marquee = null;
        m.box.destroy();
        const w = this.cameras.main.getWorldPoint(ptr.x, ptr.y);
        this.selection = selectInRect(this.project, rectFrom(m.from, w));
        this.refreshSelectTools();
      }
    });

    // Ctrl/⌘ + C / V. A focused text box keeps its own keys (it stops them).
    this.input.keyboard?.on('keydown', (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || !this.project || this.panel.length > 0) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const k = e.key.toLowerCase();
      if (k === 'c') this.copy();
      else if (k === 'v') {
        e.preventDefault();
        this.paste();
      }
    });
  }

  private isSelected(one: CreatorSelection): boolean {
    return (
      one.pieces.every((id) => this.selection.pieces.includes(id)) &&
      one.plots.every((i) => this.selection.plots.includes(i))
    );
  }

  private moveStart(at: Vec2): MoveStart {
    const p = this.project!;
    const pieces = new Map<string, Vec2>();
    for (const id of this.selection.pieces) {
      const pc = p.pieces.find((x) => x.id === id);
      if (pc) pieces.set(id, { x: pc.x, y: pc.y });
    }
    const plots = new Map<number, Vec2>();
    for (const i of this.selection.plots) if (p.plots[i]) plots.set(i, { ...p.plots[i] });
    return { at: { x: at.x, y: at.y }, pieces, plots };
  }

  /** Drag the whole selection; a lone water/river piece snaps to its grid. */
  private moveSelectionTo(w: Vec2): void {
    const m = this.moving;
    const p = this.project;
    if (!m || !p) return;
    let dx = Math.round(w.x - m.at.x);
    let dy = Math.round(w.y - m.at.y);
    if (m.pieces.size === 1 && m.plots.size === 0) {
      const [id, from] = [...m.pieces][0];
      const pc = p.pieces.find((x) => x.id === id);
      const grid = pc ? snapGrid(pc.key, pc.scale) : null;
      if (grid) {
        const at = snapPoint(from.x + dx, from.y + dy, grid);
        dx = at.x - from.x;
        dy = at.y - from.y;
      }
    }
    for (const [id, from] of m.pieces) {
      const pc = p.pieces.find((x) => x.id === id);
      if (!pc) continue;
      pc.x = from.x + dx;
      pc.y = from.y + dy;
      const obj = this.pieceImages.get(id);
      if (obj) placePiece(obj, pc.x, pc.y);
    }
    for (const [i, from] of m.plots) {
      p.plots[i] = { x: from.x + dx, y: from.y + dy };
      this.plotImages[i]?.setPosition(p.plots[i].x, p.plots[i].y);
    }
    this.dirty = true;
    this.drawOutline();
  }

  // ── Copy / paste ───────────────────────────────────────────────────────────

  /** Ctrl+C: remember the selection (nothing selected: nothing happens). */
  copy(): void {
    if (!this.project || selectionSize(this.selection) === 0) return;
    const clip = copySelection(this.project, this.selection);
    if (!clip) return;
    CreatorEditorScene.clip = clip;
    creatorFlash(this, `Copied ${selectionSize(this.selection)}`, 120);
  }

  /** Ctrl+V: the copies, centred on the mouse (or the view), selected. */
  paste(): void {
    const p = this.project;
    const clip = CreatorEditorScene.clip;
    if (!p || !clip) return;
    const ptr = this.input.activePointer;
    const cam = this.cameras.main;
    const at =
      ptr.x > SIDE_W ? cam.getWorldPoint(ptr.x, ptr.y) : { x: cam.midPoint.x, y: cam.midPoint.y };
    const out = pasteClip(clip, { x: Math.round(at.x), y: Math.round(at.y) }, newPieceId);
    if (p.pieces.length + out.pieces.length > MAX_PIECES) {
      creatorFlash(this, `A level can have ${MAX_PIECES} decorations at most`);
      return;
    }
    if (p.plots.length + out.plots.length > MAX_PLOTS) {
      creatorFlash(this, `A level can have ${MAX_PLOTS} plots at most`);
      return;
    }
    const firstPlot = p.plots.length;
    p.pieces.push(...out.pieces);
    p.plots.push(...out.plots);
    this.redrawPieces();
    this.redrawPlots();
    this.dirty = true;
    if (!this.selectMode) this.setSelectMode(true);
    this.selection = {
      pieces: out.pieces.map((x) => x.id),
      plots: out.plots.map((_, i) => firstPlot + i),
    };
    this.refreshSelectTools();
  }

  /** Add a piece (or a plot) where it was dropped. */
  place(key: string, x: number, y: number): void {
    const p = this.project;
    if (!p) return;
    if (key === 'plot') {
      if (p.plots.length >= MAX_PLOTS) {
        creatorFlash(this, `A level can have ${MAX_PLOTS} plots at most`);
        return;
      }
      p.plots.push({ x, y });
      this.redrawPlots();
    } else {
      if (p.pieces.length >= MAX_PIECES) {
        creatorFlash(this, `A level can have ${MAX_PIECES} decorations at most`);
        return;
      }
      const item = CREATOR_ITEMS.find((i) => i.key === key);
      const at = snapPoint(x, y, snapGrid(key, 1));
      const piece: CreatorPiece = {
        id: newPieceId(),
        layer: item ? layerFor(item) : 'decor',
        key,
        x: at.x,
        y: at.y,
        scale: 1,
        rotation: 0,
      };
      if (key === TEXT_KEY) piece.text = TEXT_DEFAULT;
      p.pieces.push(piece);
      this.addPieceImage(piece, p.pieces.length - 1);
    }
    this.dirty = true;
  }

  private hitTest(x: number, y: number): CreatorSelection | null {
    const p = this.project!;
    // Textboxes are on top of everything, so they are hit first.
    const ordered = p.pieces
      .map((pc, i) => ({ pc, i }))
      .sort((a, b) => PIECE_DEPTH[b.pc.layer] - PIECE_DEPTH[a.pc.layer] || b.i - a.i);
    for (const { pc } of ordered.filter((o) => o.pc.layer === 'text')) {
      if (this.pieceImages.get(pc.id)?.getBounds().contains(x, y))
        return { pieces: [pc.id], plots: [] };
    }
    for (let i = p.plots.length - 1; i >= 0; i--) {
      if (this.plotImages[i]?.getBounds().contains(x, y)) return { pieces: [], plots: [i] };
    }
    for (const { pc } of ordered.filter((o) => o.pc.layer !== 'text')) {
      if (this.pieceImages.get(pc.id)?.getBounds().contains(x, y))
        return { pieces: [pc.id], plots: [] };
    }
    return null;
  }

  /** The one selected piece, when exactly one piece (and no plot) is selected. */
  private selectedPiece(): CreatorPiece | undefined {
    const s = this.selection;
    if (s.pieces.length !== 1 || s.plots.length > 0) return undefined;
    return this.project?.pieces.find((p) => p.id === s.pieces[0]);
  }

  private drawOutline(): void {
    const g = this.outline;
    if (!g) return;
    g.clear();
    g.lineStyle(4 / this.cameras.main.zoom, 0x3fd16a, 1);
    const objs: { getBounds(): Phaser.Geom.Rectangle }[] = [
      ...this.selection.pieces.flatMap((id) => this.pieceImages.get(id) ?? []),
      ...this.selection.plots.flatMap((i) => this.plotImages[i] ?? []),
    ];
    for (const o of objs) {
      const b = o.getBounds();
      g.strokeRect(b.x - 4, b.y - 4, b.width + 8, b.height + 8);
    }
  }

  // ── Select mode and its tools ──────────────────────────────────────────────

  setSelectMode(on: boolean): void {
    this.selectMode = on;
    if (!on) this.selection = EMPTY_SELECTION;
    this.selectBtn?.bg.setFillStyle(on ? 0x3fd16a : 0x262234);
    this.refreshSelectTools();
  }

  private refreshSelectTools(): void {
    for (const o of this.selectTools) o.destroy();
    this.selectTools = [];
    this.drawOutline();
    const s = this.selection;
    if (!this.selectMode || selectionSize(s) === 0) {
      this.closeBar();
      return;
    }
    const push = (b: { bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text }) =>
      this.selectTools.push(b.bg, b.text);
    const tool = (
      x: number,
      label: string,
      name: string,
      width: number,
      fill: number,
      on: () => void,
      fontSize = 22,
    ) =>
      push(
        creatorButton(
          this,
          x,
          40,
          label,
          { name, width, height: 50, fontSize, depth: UI_DEPTH, fill },
          on,
        ),
      );
    // Left to right: ▲ ▼ FLIP DELETE 🎨 (the arrows and FLIP need a piece).
    tool(700, 'DELETE', 'creator-delete', 130, 0xb23a3a, () => this.deleteSelected());
    if (s.pieces.length > 0) {
      tool(582, 'FLIP', 'creator-flip', 86, 0x4a6fa5, () => this.flipSelected(), 20);
      tool(508, '▼', 'creator-layer-down', 50, 0x262234, () => this.layerSelected('back'));
      tool(452, '▲', 'creator-layer-up', 50, 0x262234, () => this.layerSelected('front'));
    }
    const one = this.selectedPiece();
    if (one) {
      // One piece only: a group has too many colours to recolour at once.
      tool(840, '🎨', 'creator-color', 64, 0x7b6cff, () => this.openColorMenu(), 26);
      push(
        creatorButton(
          this,
          370,
          300,
          'SCALE',
          {
            name: 'creator-scale',
            width: 120,
            height: 50,
            fontSize: 18,
            depth: UI_DEPTH,
            fill: this.barTool === 'scale' ? CREATOR_TEAL : 0x262234,
          },
          () => this.toggleBar('scale'),
        ),
      );
      push(
        creatorButton(
          this,
          370,
          362,
          'ROTATE',
          {
            name: 'creator-rotate',
            width: 120,
            height: 50,
            fontSize: 18,
            depth: UI_DEPTH,
            fill: this.barTool === 'rotate' ? CREATOR_TEAL : 0x262234,
          },
          () => this.toggleBar('rotate'),
        ),
      );
      if (one.key === TEXT_KEY) {
        push(
          creatorButton(
            this,
            370,
            424,
            'EDIT',
            {
              name: 'creator-text-edit',
              width: 120,
              height: 50,
              fontSize: 18,
              depth: UI_DEPTH,
              fill: CREATOR_ORANGE,
            },
            () => this.openTextEditor(one),
          ),
        );
      }
      if (this.barTool) this.openBar(this.barTool);
    } else {
      this.closeBar();
    }
  }

  private deleteSelected(): void {
    const s = this.selection;
    const p = this.project;
    if (!p || selectionSize(s) === 0) return;
    const ids = new Set(s.pieces);
    const plots = new Set(s.plots);
    p.pieces = p.pieces.filter((x) => !ids.has(x.id));
    p.plots = p.plots.filter((_, i) => !plots.has(i));
    this.selection = EMPTY_SELECTION;
    this.redrawPieces();
    this.redrawPlots();
    this.dirty = true;
    this.refreshSelectTools();
  }

  /** FLIP: mirror the selected piece(s). */
  flipSelected(): void {
    const p = this.project;
    if (!p || this.selection.pieces.length === 0) return;
    // In place: the SCALE / ROTATE bar and EDIT hold these very objects.
    const flipped = flipPieces(p.pieces, this.selection.pieces);
    p.pieces.forEach((pc, i) => {
      if (flipped[i].flip) pc.flip = true;
      else delete pc.flip;
      const obj = this.pieceImages.get(pc.id);
      if (obj && this.selection.pieces.includes(pc.id)) sizePiece(obj, pc);
    });
    this.dirty = true;
    this.drawOutline();
  }

  /**
   * ▲ / ▼: to the front or the back of the piece's layer; once there, a
   * decoration climbs above the track (and a top piece drops under it).
   */
  layerSelected(to: 'front' | 'back'): void {
    const p = this.project;
    if (!p || this.selection.pieces.length === 0) return;
    const { pieces: next, crossed } = stepLayer(p.pieces, this.selection.pieces, to);
    // In place: the SCALE / ROTATE bar and EDIT hold these very objects.
    const byId = new Map(p.pieces.map((pc) => [pc.id, pc]));
    p.pieces = next.map((n) => {
      const pc = byId.get(n.id) ?? n;
      pc.layer = n.layer;
      return pc;
    });
    p.pieces.forEach((pc, i) =>
      this.pieceImages.get(pc.id)?.setDepth(PIECE_DEPTH[pc.layer] + i * 1e-5),
    );
    if (crossed === 'top') creatorFlash(this, 'Above the track', 120);
    if (crossed === 'decor') creatorFlash(this, 'Under the track', 120);
    this.dirty = true;
  }

  /** A textbox's words: a DOM box over the canvas, DONE to keep them. */
  private openTextEditor(pc: CreatorPiece): void {
    this.closePanel();
    const objs = this.panel;
    objs.push(
      this.add
        .rectangle(640, 360, 1280, 720, 0x000000, 0.6)
        .setDepth(UI_DEPTH + 20)
        .setInteractive(),
      this.add
        .rectangle(790, 330, 600, 230, CREATOR_PANEL, 1)
        .setStrokeStyle(3, CREATOR_ORANGE)
        .setDepth(UI_DEPTH + 21),
      this.add
        .text(790, 250, 'TEXTBOX', { fontSize: '26px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(UI_DEPTH + 22),
    );
    let value = pc.text ?? '';
    const input = canvasTextInput(
      this,
      { x: 520, y: 290, w: 540, h: 50 },
      { maxLength: TEXT_MAX, placeholder: 'Type your text', testId: 'creator-text-input', value },
      (v) => (value = v),
    );
    const done = creatorButton(
      this,
      790,
      395,
      'DONE',
      { name: 'creator-text-done', width: 140, height: 50, depth: UI_DEPTH + 22 },
      () => {
        input.remove();
        pc.text = cleanText(value) || TEXT_DEFAULT;
        this.repaint(pc);
        this.closePanel();
      },
    );
    objs.push(done.bg, done.text);
    input.focus();
  }

  private toggleBar(tool: 'scale' | 'rotate'): void {
    this.barTool = this.barTool === tool ? null : tool;
    this.refreshSelectTools();
    if (!this.barTool) this.closeBar();
  }

  private closeBar(): void {
    for (const o of this.bar) o.destroy();
    this.bar = [];
  }

  /** The scale / rotate bar: ◀ [track + knob] ▶ along the bottom of the map. */
  private openBar(tool: 'scale' | 'rotate'): void {
    this.closeBar();
    const pc = this.selectedPiece();
    if (!pc) return;
    const X0 = 500;
    const X1 = 1060;
    const Y = 676;
    const min = tool === 'scale' ? SCALE_MIN : 0;
    const max = tool === 'scale' ? SCALE_MAX : 360;
    const step = tool === 'scale' ? 0.1 : 15;
    const get = () => (tool === 'scale' ? pc.scale : pc.rotation);
    const set = (v: number) => {
      const clamped = Math.max(min, Math.min(max, v));
      const rounded = tool === 'scale' ? Math.round(clamped * 10) / 10 : Math.round(clamped);
      if (tool === 'scale') pc.scale = rounded;
      else pc.rotation = rounded;
      const img = this.pieceImages.get(pc.id);
      if (img) sizePiece(img, pc);
      this.dirty = true;
      draw();
      this.drawOutline();
    };
    const back = this.add
      .rectangle((X0 + X1) / 2, Y, X1 - X0 + 170, 70, 0x0b0a12, 0.85)
      .setDepth(UI_DEPTH);
    const track = this.add
      .rectangle((X0 + X1) / 2, Y, X1 - X0, 10, 0x3a3f47)
      .setDepth(UI_DEPTH + 1)
      .setInteractive();
    const knob = this.add
      .circle(X0, Y, 14, CREATOR_TEAL)
      .setDepth(UI_DEPTH + 2)
      .setInteractive({ draggable: true });
    knob.setName('creator-bar-knob');
    const label = this.add
      .text((X0 + X1) / 2, Y - 26, '', { fontSize: '16px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(UI_DEPTH + 2)
      .setName('creator-bar-value');
    const draw = () => {
      knob.x = X0 + ((get() - min) / (max - min)) * (X1 - X0);
      label.setText(tool === 'scale' ? `SCALE ${get().toFixed(1)}` : `ROTATE ${get()}°`);
    };
    const toValue = (x: number) =>
      min + ((Math.max(X0, Math.min(X1, x)) - X0) / (X1 - X0)) * (max - min);
    knob.on('drag', (_p: Phaser.Input.Pointer, dx: number) => set(toValue(dx)));
    track.on('pointerdown', (p: Phaser.Input.Pointer) => set(toValue(p.x)));
    const prev = creatorButton(
      this,
      X0 - 50,
      Y,
      '◀',
      { name: 'creator-bar-prev', width: 50, height: 50, depth: UI_DEPTH + 1 },
      () => set(get() - step),
    );
    const next = creatorButton(
      this,
      X1 + 50,
      Y,
      '▶',
      { name: 'creator-bar-next', width: 50, height: 50, depth: UI_DEPTH + 1 },
      () => set(get() + step),
    );
    this.bar.push(back, track, knob, label, prev.bg, prev.text, next.bg, next.text);
    draw();
  }

  // ── Colour menu ────────────────────────────────────────────────────────────

  private closePanel(): void {
    for (const o of this.panel) o.destroy();
    this.panel = [];
  }

  private openColorMenu(): void {
    const pc = this.selectedPiece();
    if (!pc) return;
    this.closePanel();
    const groups = textureColorGroups(this, pc.key);
    const objs = this.panel;
    objs.push(
      this.add
        .rectangle(640, 360, 1280, 720, 0x000000, 0.6)
        .setDepth(UI_DEPTH + 20)
        .setInteractive(),
    );
    objs.push(
      this.add
        .rectangle(790, 360, 640, 460, CREATOR_PANEL, 1)
        .setStrokeStyle(3, 0x7b6cff)
        .setDepth(UI_DEPTH + 21),
    );
    objs.push(
      this.add
        .text(790, 160, 'COLOURS', { fontSize: '28px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(UI_DEPTH + 22),
    );
    if (groups.length === 0) {
      objs.push(
        this.add
          .text(790, 330, 'This piece has no colours to change', {
            fontSize: '18px',
            color: '#9aa6ad',
          })
          .setOrigin(0.5)
          .setDepth(UI_DEPTH + 22),
      );
    }
    groups.forEach((g, i) => {
      const x = 790 - ((groups.length - 1) * 80) / 2 + i * 80;
      const now = pc.colors?.[g] ?? g;
      const sq = this.add
        .rectangle(x, 260, 64, 64, parseInt(now.slice(1), 16))
        .setStrokeStyle(3, 0xffffff)
        .setDepth(UI_DEPTH + 22)
        .setInteractive({ useHandCursor: true })
        .setName(`creator-color-group-${i}`);
      sq.on('pointerup', () => this.openPalette(pc, g));
      objs.push(sq);
    });
    const reset = creatorButton(
      this,
      700,
      540,
      'RESET',
      { name: 'creator-color-reset', width: 140, height: 50, depth: UI_DEPTH + 22, fill: 0x5c6168 },
      () => {
        delete pc.colors;
        this.repaint(pc);
        this.openColorMenu();
      },
    );
    const done = creatorButton(
      this,
      880,
      540,
      'DONE',
      { name: 'creator-color-done', width: 140, height: 50, depth: UI_DEPTH + 22 },
      () => this.closePanel(),
    );
    objs.push(reset.bg, reset.text, done.bg, done.text);
  }

  private openPalette(pc: CreatorPiece, group: string): void {
    const objs = this.panel;
    const SW = 34;
    const x0 = 790 - (10 * (SW + 4)) / 2 + SW / 2;
    const y0 = 330;
    PALETTE.forEach((c, i) => {
      const sw = this.add
        .rectangle(
          x0 + (i % 10) * (SW + 4),
          y0 + Math.floor(i / 10) * (SW + 4) - 30,
          SW,
          SW,
          parseInt(c.slice(1), 16),
        )
        .setStrokeStyle(1, 0x000000, 0.5)
        .setDepth(UI_DEPTH + 30)
        .setInteractive({ useHandCursor: true })
        .setName(`creator-swatch-${i}`);
      sw.on('pointerup', () => {
        pc.colors = { ...(pc.colors ?? {}), [group]: c };
        this.repaint(pc);
        this.openColorMenu();
      });
      objs.push(sw);
    });
    objs.push(
      this.add
        .rectangle(790, 380, 400, 330, CREATOR_PANEL, 1)
        .setStrokeStyle(2, 0xffffff, 0.5)
        .setDepth(UI_DEPTH + 29),
    );
  }

  private repaint(pc: CreatorPiece): void {
    const i = this.project!.pieces.indexOf(pc);
    this.pieceImages.get(pc.id)?.destroy();
    this.addPieceImage(pc, i);
    this.dirty = true;
    this.drawOutline();
  }

  // ── Zoom ───────────────────────────────────────────────────────────────────

  private zoomBy(dir: 1 | -1): void {
    this.zoomIndex = Math.max(0, Math.min(ZOOMS.length - 1, this.zoomIndex + dir));
    this.applyZoom(false);
  }

  private applyZoom(centre: boolean): void {
    const cam = this.cameras.main;
    const mid = centre ? { x: 640, y: 360 } : { x: cam.midPoint.x, y: cam.midPoint.y };
    cam.setZoom(ZOOMS[this.zoomIndex]);
    cam.centerOn(mid.x, mid.y);
    this.drawOutline();
  }

  // ── TEST ───────────────────────────────────────────────────────────────────

  private async test(): Promise<void> {
    const p = this.project;
    if (!p || this.busy) return;
    if (p.waves.length === 0) {
      creatorFlash(this, NO_WAVES);
      return;
    }
    // A plot dragged off the map is unreachable in the game.
    if (onMapPlots(p).length === 0) {
      creatorFlash(this, NO_PLOTS);
      return;
    }
    await this.saveAnd(() => {
      setCreatorLevel(projectToLevel(this.project!));
      this.scene.start('GameScene', {
        levelId: projectToLevel(this.project!).id,
        creatorId: this.projectId,
      });
      this.scene.launch('HudScene', {
        levelId: projectToLevel(this.project!).id,
        creatorId: this.projectId,
      });
    });
  }
}
